/**
 * The second step of a bulk invite, shared by the `Pools` and `Chats` tabs
 * (ISSUE-197, ISSUE-1097). Once a source is picked it runs
 * `/invite/bulk-preview` for that source and hands the resolved candidates,
 * and the people already in or invited, to `InviteBulkConfirm`, which owns the
 * unselect step and the count confirm. It is only mounted once a source is
 * picked, so it never registers a preview for no source at all.
 *
 * A failed preview is deliberately kept apart from an empty one. Both return
 * no candidates, so collapsing them would tell a fan whose connection dropped
 * that "Everyone here is already in", which is a confident wrong answer they
 * cannot retry out of. The same holds for a chat the server refuses: a refusal
 * is drawn as a failure, never as everyone already being in.
 *
 * It renders a fragment. The calling tab supplies its own wrapper.
 */
import { useInviteBulkPreview } from "@sportsmet/pool-sdk/private";
import { Button, InlineBanner, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { InviteBulkConfirm } from "./InviteBulkConfirm";
import {
  INVITE_BULK_CANCEL_LABEL,
  INVITE_BULK_RETRY_LABEL,
  PREVIEW_EMPTY_TITLE,
} from "./inviteSheetCopy";

export type typeInviteSourcePreviewCopy = {
  loadingLabel: string;
  failedMessage: string;
  emptyBody: string;
};

export type typeInviteSourcePreviewProps = {
  poolId: string;
  source: "pool" | "chat";
  sourceId: string;
  sourceName: string;
  copy: typeInviteSourcePreviewCopy;
  onBack: () => void;
};

export const InviteSourcePreview = ({
  poolId,
  source,
  sourceId,
  sourceName,
  copy,
  onBack,
}: typeInviteSourcePreviewProps): ReactNode => {
  // The preview is a snapshot for the life of this step. gcTime 0 drops it the
  // moment the step unmounts, so picking the same source again after a send
  // reads the roster afresh and never offers the people just invited. No
  // refetch on focus or reconnect, and no send-time invalidation: any of those
  // could refetch while the sent note is on screen and, with nobody left to
  // invite, swap the note for the empty state. `Try again` still refetches.
  const preview = useInviteBulkPreview(
    { poolId, source, sourceId },
    { gcTime: 0, refetchOnWindowFocus: false, refetchOnReconnect: false },
  );

  const previewFailed =
    !preview.isLoading && (preview.isError || Boolean(preview.data?.error));
  const candidates = preview.data?.data?.candidates ?? [];
  const alreadyIn = preview.data?.data?.alreadyIn ?? [];

  return (
    <>
      {preview.isLoading ? (
        <div data-testid="invite-preview-loading">
          <Skeleton shape="control" label={copy.loadingLabel} />
        </div>
      ) : null}
      {previewFailed ? (
        <>
          <div data-testid="invite-preview-failed">
            <InlineBanner
              variant="warning"
              message={copy.failedMessage}
              action={{
                label: INVITE_BULK_RETRY_LABEL,
                onAction: () => {
                  void preview.refetch();
                },
              }}
            />
          </div>
          <Button
            variant="secondary"
            data-testid="invite-preview-back"
            onClick={onBack}
          >
            {INVITE_BULK_CANCEL_LABEL}
          </Button>
        </>
      ) : null}
      {!preview.isLoading && !previewFailed && candidates.length === 0 ? (
        <>
          <div
            className="flex flex-col gap-1"
            data-testid="invite-preview-empty"
          >
            <p className="text-body text-ink">{PREVIEW_EMPTY_TITLE}</p>
            <p className="text-caption text-ink-secondary">{copy.emptyBody}</p>
          </div>
          <Button
            variant="secondary"
            data-testid="invite-preview-back"
            onClick={onBack}
          >
            {INVITE_BULK_CANCEL_LABEL}
          </Button>
        </>
      ) : null}
      {!preview.isLoading && !previewFailed && candidates.length > 0 ? (
        <InviteBulkConfirm
          poolId={poolId}
          source={source}
          sourceId={sourceId}
          sourceName={sourceName}
          candidates={candidates.map((candidate) => ({
            userId: candidate.userId,
            displayName: candidate.displayName,
            avatarUrl: candidate.avatarUrl ?? null,
          }))}
          alreadyIn={alreadyIn}
          onDone={onBack}
        />
      ) : null}
    </>
  );
};
